import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import {
  SectionHeading,
  TextField,
  Toggle,
  colors,
} from 'src/components/app-ui'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import useQueuedSave, { jsonEquals } from 'src/hooks/useQueuedSave'
import UserType from 'src/model/user-types'

const SETTING_KEY = 'webhookAnswer'

const TRIGGERS = [
  {
    trigger: 0,
    title: 'Page completed',
    description: 'Fires when a learner finishes a page.',
  },
  {
    trigger: 1,
    title: 'Course completed',
    description: 'Fires when a learner finishes the last page.',
  },
]

// The course saga skips the callback when a save fails; give up after this.
const SAVE_TIMEOUT = 15000

const useStyles = makeStyles({
  url: {
    maxWidth: 800,
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 0',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
  },
  desc: {
    marginTop: 4,
    fontSize: '13px',
    color: colors.muted,
  },
  upgrade: {
    fontSize: '13px',
    color: colors.muted,
    '& a': {
      color: colors.primary,
      fontWeight: 500,
    },
  },
})

/** The saved setting as { url, isActive, triggers: [page, course] }. */
const readWebhook = value => ({
  url: value?.url || '',
  isActive: true,
  triggers: TRIGGERS.map(({ trigger }) => ({
    trigger,
    isActive: Boolean(
      value?.triggers?.find(item => item.trigger === trigger)?.isActive,
    ),
  })),
})

/** Webhooks start at Pro v2; Basic and Plus can't edit them. */
const canUseWebhooks = () =>
  Number(window.CONSTANTS.USER_ROLE_ID) !== 1 &&
  UserType[window.CONSTANTS.USER_ROLE_ID] !== 'Plus'

/**
 * Course › Settings › Webhook: the endpoint and which learner events it gets.
 * Every change saves right away (quick toggles queue up, see useQueuedSave).
 */
const WebhookSection = () => {
  const classes = useStyles()
  const { execute, update, exists, value } = useCourseSettings(SETTING_KEY)
  const allowed = canUseWebhooks()

  const persist = next =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(reject, SAVE_TIMEOUT)
      const done = () => {
        clearTimeout(timer)
        resolve()
      }
      if (exists) update(next, done)
      else execute(next, done)
    })

  const [webhook, save] = useQueuedSave(readWebhook(value), persist, jsonEquals)
  const [url, setUrl] = useState(webhook.url)

  useEffect(() => {
    setUrl(webhook.url)
  }, [webhook.url])

  const toggleTrigger = (trigger, isActive) =>
    save({
      ...webhook,
      triggers: webhook.triggers.map(item =>
        item.trigger === trigger ? { ...item, isActive } : item,
      ),
    })

  const isOn = trigger =>
    Boolean(webhook.triggers.find(item => item.trigger === trigger)?.isActive)

  return (
    <>
      <SectionHeading
        title="Webhook"
        description="Notify external services when learners interact with this course."
      />
      <div className={classes.url}>
        <TextField
          placeholder="Webhook URL"
          value={url}
          disabled={!allowed}
          onChange={event => setUrl(event.target.value)}
          onBlur={() => url !== webhook.url && save({ ...webhook, url })}
          onKeyDown={event => event.key === 'Enter' && event.target.blur()}
        />
      </div>
      <div className={classes.rows}>
        {TRIGGERS.map(({ trigger, title, description }) => (
          <div key={trigger} className={classes.row}>
            <div>
              <div className={classes.title}>{title}</div>
              <div className={classes.desc}>{description}</div>
            </div>
            <Toggle
              checked={isOn(trigger)}
              disabled={!allowed}
              onChange={next => toggleTrigger(trigger, next)}
            />
          </div>
        ))}
      </div>
      {!allowed && (
        <div className={classes.upgrade}>
          Webhooks are available on Pro and higher plans.{' '}
          <RouterLink to="/pricing">Upgrade</RouterLink>
        </div>
      )}
    </>
  )
}

export default WebhookSection
